import { unwrap, type components } from "@sagot/api-client";
import { useMutation, useQuery, useQueryClient, type UseQueryResult } from "@tanstack/react-query";
import { usePermissions } from "../../../session/permissions";
import { useApi } from "../../../session/session-provider";
import { useWorkspaceId } from "../../../session/workspace-context";
import { dkKeys } from "../shared/keys";
import type { AccessMatrixOut, AgentSummaryOut, EndUserOut, EndUserStatus, IdentityProviderOut } from "../shared/types";

type Schemas = components["schemas"];
export type AudienceIn = Schemas["AudienceIn"];
export type AccessEntry = Schemas["AccessEntry"];
export type ProviderCreate = Schemas["ProviderCreate"];
export type ProviderUpdate = Schemas["ProviderUpdate"];
export type ImportResult = Schemas["ImportResult"];
export type EndUserUpdate = Schemas["EndUserUpdate"];

export const END_USERS_PAGE_SIZE = 10;

/** Whether the signed-in member may change audiences, the matrix, end users and IdPs. */
export function useCanManageAudiences(): boolean {
  const permissions = usePermissions();
  return permissions.can("audiences.manage") && !permissions.readOnly;
}

/**
 * Agents for the "Agents:" line and the playground link. Own key: `["ws", id, "agents"]` is also
 * used by other groups with a different cache shape.
 */
export function useAudienceAgents(): UseQueryResult<AgentSummaryOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: ["ws", wsId, "audiences-page", "agents"],
    queryFn: async () => (await unwrap(api.GET("/v1/agents"))).items,
    staleTime: 60_000,
  });
}

export function useAccessMatrix(): UseQueryResult<AccessMatrixOut> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: dkKeys.accessMatrix(wsId), queryFn: () => unwrap(api.GET("/v1/access-matrix")) });
}

export interface EndUserFilters {
  q: string;
  audience: number | null;
  page: number;
}

export function useEndUsers({ q, audience, page }: EndUserFilters): UseQueryResult<{ items: EndUserOut[]; total: number }> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({
    queryKey: [...dkKeys.endUsers(wsId, q, page), audience],
    queryFn: () =>
      unwrap(
        api.GET("/v1/end-users", {
          params: { query: { q: q || null, audience, limit: END_USERS_PAGE_SIZE, offset: (page - 1) * END_USERS_PAGE_SIZE } },
        }),
      ),
    placeholderData: (previous) => previous,
  });
}

export function useIdentityProviders(): UseQueryResult<IdentityProviderOut[]> {
  const api = useApi();
  const wsId = useWorkspaceId();
  return useQuery({ queryKey: dkKeys.identityProviders(wsId), queryFn: () => unwrap(api.GET("/v1/identity-providers")) });
}

/** Invalidates audience-related reads (labels, stats, matrix, end users, providers). */
function useInvalidateAudiences() {
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return async () => {
    await Promise.all(
      [dkKeys.audiences(wsId), dkKeys.accessMatrix(wsId), ["ws", wsId, "end-users"], dkKeys.identityProviders(wsId)].map((queryKey) =>
        queryClient.invalidateQueries({ queryKey }),
      ),
    );
  };
}

export function useSaveAudiences() {
  const api = useApi();
  const invalidate = useInvalidateAudiences();
  return useMutation({
    mutationFn: (audiences: AudienceIn[]) => unwrap(api.PUT("/v1/audiences", { body: { audiences } })),
    onSuccess: invalidate,
  });
}

export function useSaveAccessMatrix() {
  const api = useApi();
  const queryClient = useQueryClient();
  const wsId = useWorkspaceId();
  return useMutation({
    mutationFn: (entries: AccessEntry[]) => unwrap(api.PUT("/v1/access-matrix", { body: { entries } })),
    onSuccess: async (result) => {
      queryClient.setQueryData(dkKeys.accessMatrix(wsId), result);
      await queryClient.invalidateQueries({ queryKey: dkKeys.audiences(wsId) });
      await queryClient.invalidateQueries({ queryKey: dkKeys.sources(wsId) });
    },
  });
}

export function useUpdateEndUser() {
  const api = useApi();
  const invalidate = useInvalidateAudiences();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: EndUserUpdate }) =>
      unwrap(api.PATCH("/v1/end-users/{end_user_id}", { params: { path: { end_user_id: id } }, body })),
    onSuccess: invalidate,
  });
}

/** `POST /v1/end-users/import` with the raw CSV text (`text/csv`, not JSON-encoded). */
export function useImportEndUsers() {
  const api = useApi();
  const invalidate = useInvalidateAudiences();
  return useMutation({
    mutationFn: (csv: string) =>
      unwrap(
        api.POST("/v1/end-users/import", {
          body: csv,
          bodySerializer: (body: string) => body,
          headers: { "content-type": "text/csv" },
        }),
      ),
    onSuccess: invalidate,
  });
}

export function useSaveProvider() {
  const api = useApi();
  const invalidate = useInvalidateAudiences();
  return useMutation({
    mutationFn: ({ id, create, update }: { id?: string | undefined; create: ProviderCreate; update: ProviderUpdate }) =>
      id
        ? unwrap(api.PATCH("/v1/identity-providers/{provider_id}", { params: { path: { provider_id: id } }, body: update }))
        : unwrap(api.POST("/v1/identity-providers", { body: create })),
    onSuccess: invalidate,
  });
}

export function useToggleProvider() {
  const api = useApi();
  const invalidate = useInvalidateAudiences();
  return useMutation({
    mutationFn: ({ id, enabled }: { id: string; enabled: boolean }) =>
      unwrap(api.PATCH("/v1/identity-providers/{provider_id}", { params: { path: { provider_id: id } }, body: { enabled } })),
    onSuccess: invalidate,
  });
}

export function useDeleteProvider() {
  const api = useApi();
  const invalidate = useInvalidateAudiences();
  return useMutation({
    mutationFn: (id: string) => unwrap(api.DELETE("/v1/identity-providers/{provider_id}", { params: { path: { provider_id: id } } })),
    onSuccess: invalidate,
  });
}

export type { EndUserStatus };
